import type { CheckoutFormValuesType } from '@ecomm/checkout/shipping-schema'
import { validatePostalCode } from '@ecomm/checkout/utils'
import { useCartSetShippingAddress } from '@ecomm/data-cart'
import { useLocale } from '@ecomm/data-hooks'
import { useCurrentPromo } from '@ecomm/promotions-hooks'
import { noValue } from '@ecomm/utils'
import { useFormikContext } from 'formik'
import { useEffect } from 'react'

import { useDebounce } from './useDebounce'

/**
 * Sets shipping address on cart and fetches shipping methods when postalCode and state are set/updated
 */
export const useUpdateCartShippingAddress = () => {
  const locale = useLocale()
  const { isSubmitting, values } = useFormikContext<CheckoutFormValuesType>()
  const { variationId } = useCurrentPromo()
  const cartSetShippingAddress = useCartSetShippingAddress(variationId)

  const debouncedStreetName = useDebounce(values.streetName, 500)
  const debouncedAdditionalStreetInfo = useDebounce(
    values.additionalStreetInfo,
    500
  )

  useEffect(() => {
    const hasValidPostalCode =
      !!values.postalCode && validatePostalCode(values.postalCode, locale)
    const hasValidState = !!values.state || locale === 'en-GB'
    const shouldFetch = hasValidPostalCode && hasValidState && !isSubmitting

    shouldFetch &&
      cartSetShippingAddress({
        additionalStreetInfo: debouncedAdditionalStreetInfo,
        city: values.city,
        country: locale === 'en-US' ? 'US' : 'GB',
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
        phone: values.phone,
        postalCode: values.postalCode,
        state: values.state || noValue(),
        streetName: debouncedStreetName
      })
    // Only fetch if state, postalCode, streetName or additionalStreetInfo has changed
  }, [
    values.state,
    values.postalCode,
    debouncedStreetName,
    debouncedAdditionalStreetInfo
  ])
}
